import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  Cell,
} from 'recharts';
import {
  TrendingUp,
  ShoppingCart,
  Package,
  Boxes,
  Mail,
  Send,
  CheckCircle2,
  Clock,
  ArrowUpRight,
  AlertTriangle,
} from 'lucide-react';
import { adminDashboardApi } from '../api/operations';
import { PageHeader } from '../components/Shell';
import { StatTile } from '../components/DataTable';
import { ErrorState, Skeleton } from '../components/ui';
import { useToast } from '../components/Toast';
import { formatDate, money, number, percent } from '../lib/format';

const RANGES = [
  { key: '7', label: 'Son 7 gün' },
  { key: '30', label: 'Son 30 gün' },
  { key: '90', label: 'Son 90 gün' },
] as const;

const BAR_COLORS = ['#1d67f1', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899', '#06b6d4', '#64748b'];

export function DashboardPage() {
  const [range, setRange] = useState<'7' | '30' | '90'>('30');
  const queryClient = useQueryClient();
  const toast = useToast();

  const { from, to } = useMemo(() => {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - Number(range));
    return { from: start.toISOString(), to: end.toISOString() };
  }, [range]);

  const summary = useQuery({
    queryKey: ['dashboard', 'summary', from, to],
    queryFn: () => adminDashboardApi.summary(from, to),
  });

  const trend = useQuery({
    queryKey: ['dashboard', 'trend', from, to],
    queryFn: () =>
      adminDashboardApi.salesTrend(from, to, Number(range) > 60 ? 'week' : 'day'),
  });

  const topProducts = useQuery({
    queryKey: ['dashboard', 'top-products', from, to],
    queryFn: () => adminDashboardApi.topProducts(from, to, 8),
  });

  const topCategories = useQuery({
    queryKey: ['dashboard', 'top-categories', from, to],
    queryFn: () => adminDashboardApi.topCategories(from, to, 6),
  });

  const recentOrders = useQuery({
    queryKey: ['dashboard', 'recent-orders'],
    queryFn: () => adminDashboardApi.recentOrders(8),
  });

  const abandonedCarts = useQuery({
    queryKey: ['dashboard', 'abandoned-carts'],
    queryFn: () => adminDashboardApi.abandonedCarts(10),
  });

  const lowStock = useQuery({
    queryKey: ['dashboard', 'low-stock'],
    queryFn: () => adminDashboardApi.lowStock(8),
  });

  const sendEmailMutation = useMutation({
    mutationFn: (cartId: number) => adminDashboardApi.sendAbandonedCartEmail(cartId),
    onSuccess: () => {
      toast.success('Terk edilmiş sepet hatırlatma e-postası başarıyla gönderildi.');
      void queryClient.invalidateQueries({ queryKey: ['dashboard', 'abandoned-carts'] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard', 'summary'] });
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.error?.errors?.[0] || 'E-posta gönderilemedi.');
    },
  });

  const sendAllEmailMutation = useMutation({
    mutationFn: () => adminDashboardApi.sendAllAbandonedCartEmails(),
    onSuccess: (data: any) => {
      toast.success(data?.message || 'Terk edilmiş sepet hatırlatmaları başarıyla gönderildi.');
      void queryClient.invalidateQueries({ queryKey: ['dashboard', 'abandoned-carts'] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard', 'summary'] });
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.error?.errors?.[0] || 'Toplu e-posta gönderilemedi.');
    },
  });

  const currency = summary.data?.currencyCode ?? 'TRY';

  return (
    <div className="space-y-6">
      <PageHeader
        title="Pano"
        description="Mağazanızın güncel satış, stok, müşteri ve performans raporları"
        actions={
          <div className="flex rounded-lg border border-ink-200 bg-white p-0.5 shadow-sm">
            {RANGES.map((option) => (
              <button
                key={option.key}
                type="button"
                onClick={() => setRange(option.key)}
                className={`rounded-md px-3 py-1.5 text-xs font-medium transition ${
                  range === option.key ? 'bg-ink-900 text-white' : 'text-ink-600 hover:bg-ink-100'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        }
      />

      {summary.isError ? (
        <ErrorState error={summary.error} onRetry={() => void summary.refetch()} />
      ) : (
        <>
          {/* Ana Metrik Kartlari (8 Temel Metrik) */}
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <StatTile
              label="Günlük Satış"
              value={
                summary.isLoading ? (
                  <Skeleton className="h-7 w-28" />
                ) : (
                  money(summary.data?.dailyRevenue, currency)
                )
              }
              hint={
                summary.data
                  ? `Bugün ${number(summary.data.dailyOrderCount)} sipariş`
                  : undefined
              }
              tone="positive"
            />
            <StatTile
              label="Aylık Satış"
              value={
                summary.isLoading ? (
                  <Skeleton className="h-7 w-28" />
                ) : (
                  money(summary.data?.monthlyRevenue, currency)
                )
              }
              hint={
                summary.data
                  ? `Bu ay ${number(summary.data.monthlyOrderCount)} sipariş`
                  : undefined
              }
            />
            <StatTile
              label="Toplam Sipariş Sayısı"
              value={summary.isLoading ? <Skeleton className="h-7 w-16" /> : number(summary.data?.orderCount)}
              hint={
                summary.data
                  ? `Önceki döneme göre ${percent(summary.data.orderCountChangePercentage, 1)}`
                  : undefined
              }
            />
            <StatTile
              label="Ortalama Sepet (AOV)"
              value={
                summary.isLoading ? (
                  <Skeleton className="h-7 w-24" />
                ) : (
                  money(summary.data?.averageOrderValue, currency)
                )
              }
            />
            <StatTile
              label="Kullanıcı Sayısı"
              value={
                summary.isLoading ? <Skeleton className="h-7 w-16" /> : number(summary.data?.totalCustomerCount)
              }
              hint={
                summary.data
                  ? `Seçilen dönemde +${number(summary.data.newCustomerCount)} yeni kayıt`
                  : undefined
              }
            />
            <StatTile
              label="Aktif Ürün Sayısı"
              value={
                summary.isLoading ? <Skeleton className="h-7 w-16" /> : number(summary.data?.totalProductCount)
              }
            />
            <StatTile
              label="Toplam Stok Değeri"
              value={
                summary.isLoading ? (
                  <Skeleton className="h-7 w-28" />
                ) : (
                  money(summary.data?.totalStockValue, currency)
                )
              }
              hint="Depodaki satılabilir ürün stok karşılığı"
            />
            <StatTile
              label="Dönüşüm Oranı (Conversion Rate)"
              value={
                summary.isLoading ? (
                  <Skeleton className="h-7 w-20" />
                ) : (
                  `%${summary.data?.conversionRate ?? 0}`
                )
              }
              hint="Tamamlanan Sipariş / Toplam Sepet"
              tone="positive"
            />
          </div>

          {/* Islem Bekleyen Acil Görevler */}
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <TaskTile
              to="/siparisler?durum=Pending"
              label="Ödeme bekleyen sipariş"
              value={summary.data?.pendingOrderCount ?? 0}
            />
            <TaskTile
              to="/sevkiyat"
              label="Sevk bekleyen sipariş"
              value={summary.data?.pendingFulfillmentCount ?? 0}
            />
            <TaskTile
              to="/iadeler"
              label="Bekleyen iade talebi"
              value={summary.data?.pendingReturnCount ?? 0}
            />
            <TaskTile
              to="/yorumlar"
              label="Onay bekleyen yorum"
              value={summary.data?.pendingReviewCount ?? 0}
            />
          </div>

          {/* Satis Grafigi & Kategori Dağılımı */}
          <div className="grid gap-6 xl:grid-cols-3">
            {/* Satis Egrisi Grafigi */}
            <section className="card p-5 xl:col-span-2">
              <div className="mb-4 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-brand-600" />
                  <h2 className="text-sm font-semibold text-ink-900">Satış Eğrisi (Ciro)</h2>
                </div>
                <span className="text-xs text-ink-500">Net Ciro Trendi</span>
              </div>

              {trend.isLoading ? (
                <Skeleton className="h-72 w-full" />
              ) : trend.data && trend.data.length > 0 ? (
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={trend.data} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                      <defs>
                        <linearGradient id="revenue" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#1d67f1" stopOpacity={0.3} />
                          <stop offset="100%" stopColor="#1d67f1" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#eceef2" vertical={false} />
                      <XAxis
                        dataKey="date"
                        tickFormatter={(value: string) => formatDate(value).slice(0, 6)}
                        tick={{ fontSize: 11, fill: '#8494ab' }}
                        axisLine={false}
                        tickLine={false}
                      />
                      <YAxis
                        tick={{ fontSize: 11, fill: '#8494ab' }}
                        axisLine={false}
                        tickLine={false}
                        width={75}
                        tickFormatter={(value: number) => money(value, currency)}
                      />
                      <Tooltip
                        formatter={(value: number, name: string) =>
                          name === 'revenue' ? [money(value, currency), 'Ciro'] : [number(value), 'Sipariş']
                        }
                        labelFormatter={(label: string) => formatDate(label)}
                        contentStyle={{
                          borderRadius: 10,
                          border: '1px solid #d5dae2',
                          fontSize: 12,
                          boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                        }}
                      />
                      <Area
                        type="monotone"
                        dataKey="revenue"
                        name="Ciro"
                        stroke="#1d67f1"
                        strokeWidth={2.5}
                        fill="url(#revenue)"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <p className="py-20 text-center text-sm text-ink-500">
                  Seçilen dönemde satış kaydı bulunmuyor.
                </p>
              )}
            </section>

            {/* En Çok Satan Kategoriler Grafigi */}
            <section className="card p-5">
              <div className="mb-4 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Boxes className="h-5 w-5 text-emerald-600" />
                  <h2 className="text-sm font-semibold text-ink-900">En Çok Satan Kategoriler</h2>
                </div>
              </div>

              {topCategories.isLoading ? (
                <Skeleton className="h-72 w-full" />
              ) : topCategories.data && topCategories.data.length > 0 ? (
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={topCategories.data}
                      layout="vertical"
                      margin={{ top: 5, right: 10, left: 10, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#eceef2" horizontal={false} />
                      <XAxis type="number" hide />
                      <YAxis
                        dataKey="categoryName"
                        type="category"
                        tick={{ fontSize: 11, fill: '#475569' }}
                        width={90}
                        axisLine={false}
                        tickLine={false}
                      />
                      <Tooltip
                        formatter={(val: number) => [money(val, currency), 'Ciro']}
                        contentStyle={{ borderRadius: 8, fontSize: 12 }}
                      />
                      <Bar dataKey="revenue" radius={[0, 6, 6, 0]}>
                        {topCategories.data.map((_, index) => (
                          <Cell key={`cell-${index}`} fill={BAR_COLORS[index % BAR_COLORS.length]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <p className="py-20 text-center text-sm text-ink-500">
                  Kategori satış verisi yok.
                </p>
              )}
            </section>
          </div>

          {/* En Çok Satan Ürünler & Son Siparişler Grid */}
          <div className="grid gap-6 lg:grid-cols-2">
            {/* En çok satan ürünler */}
            <section className="card p-5">
              <div className="mb-4 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Package className="h-5 w-5 text-amber-600" />
                  <h2 className="text-sm font-semibold text-ink-900">En Çok Satan Ürünler</h2>
                </div>
                <Link to="/urunler" className="text-xs text-brand-700 hover:underline flex items-center gap-1 font-medium">
                  Tümü <ArrowUpRight className="h-3.5 w-3.5" />
                </Link>
              </div>

              {topProducts.isLoading ? (
                <Skeleton className="h-48 w-full" />
              ) : topProducts.data && topProducts.data.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-ink-200 text-left text-xs uppercase tracking-wide text-ink-500">
                        <th className="pb-2 font-semibold">Ürün</th>
                        <th className="pb-2 text-right font-semibold">Satılan Adet</th>
                        <th className="pb-2 text-right font-semibold">Ciro</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-ink-100">
                      {topProducts.data.map((product) => (
                        <tr key={product.productId} className="hover:bg-ink-50/50 transition">
                          <td className="py-2.5">
                            <div className="flex items-center gap-3">
                              <div className="h-9 w-9 shrink-0 overflow-hidden rounded-md border border-ink-200 bg-ink-50">
                                {product.imageUrl ? (
                                  <img src={product.imageUrl} alt="" className="h-full w-full object-cover" />
                                ) : (
                                  <div className="flex h-full items-center justify-center text-ink-400">
                                    <Package className="h-4 w-4" />
                                  </div>
                                )}
                              </div>
                              <div className="min-w-0 max-w-[180px]">
                                <p className="truncate font-medium text-ink-800">{product.title}</p>
                                {product.sku ? (
                                  <p className="truncate text-xs text-ink-400">{product.sku}</p>
                                ) : null}
                              </div>
                            </div>
                          </td>
                          <td className="py-2.5 text-right font-medium text-ink-700">
                            {number(product.quantitySold)} adet
                          </td>
                          <td className="py-2.5 text-right font-semibold text-ink-900">
                            {money(product.revenue, currency)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="py-12 text-center text-sm text-ink-500">Bu dönemde satış yok.</p>
              )}
            </section>

            {/* Son Siparişler */}
            <section className="card p-5">
              <div className="mb-4 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <ShoppingCart className="h-5 w-5 text-indigo-600" />
                  <h2 className="text-sm font-semibold text-ink-900">Son Siparişler</h2>
                </div>
                <Link to="/siparisler" className="text-xs text-brand-700 hover:underline flex items-center gap-1 font-medium">
                  Siparişlere Git <ArrowUpRight className="h-3.5 w-3.5" />
                </Link>
              </div>

              {recentOrders.isLoading ? (
                <Skeleton className="h-48 w-full" />
              ) : recentOrders.data && recentOrders.data.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-ink-200 text-left text-xs uppercase tracking-wide text-ink-500">
                        <th className="pb-2 font-semibold">Sipariş No</th>
                        <th className="pb-2 font-semibold">Müşteri</th>
                        <th className="pb-2 font-semibold">Durum</th>
                        <th className="pb-2 text-right font-semibold">Tutar</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-ink-100">
                      {recentOrders.data.map((order) => (
                        <tr key={order.orderId} className="hover:bg-ink-50/50 transition">
                          <td className="py-2.5">
                            <Link
                              to={`/siparisler/${order.orderId}`}
                              className="font-semibold text-brand-700 hover:underline"
                            >
                              #{order.orderNumber}
                            </Link>
                            <p className="text-[11px] text-ink-400">{formatDate(order.createdAt)}</p>
                          </td>
                          <td className="py-2.5 max-w-[140px]">
                            <p className="truncate text-ink-800 font-medium">{order.customerName}</p>
                            <p className="truncate text-xs text-ink-400">{order.customerEmail}</p>
                          </td>
                          <td className="py-2.5">
                            <OrderStatusTag status={order.status} financial={order.financialStatus} />
                          </td>
                          <td className="py-2.5 text-right font-semibold text-ink-900">
                            {money(order.grandTotal, currency)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="py-12 text-center text-sm text-ink-500">Henüz sipariş bulunmuyor.</p>
              )}
            </section>
          </div>

          {/* Terk Edilen Sepetler Yönetimi & Manuel E-Posta Gönderme */}
          <section className="card p-5 border-amber-200/60 bg-amber-50/20">
            <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <Mail className="h-5 w-5 text-amber-600" />
                  <h2 className="text-base font-semibold text-ink-900">Terk Edilen Sepetler</h2>
                </div>
                <p className="text-xs text-ink-600 mt-1">
                  Müşterilerin sepetinde bıraktığı ürünler. Arka planda otomatik gönderim yerine admin paneli üzerinden{' '}
                  <span className="font-semibold text-ink-800">manuel olarak</span> e-posta hatırlatması gönderebilirsiniz.
                </p>
              </div>
              {abandonedCarts.data && abandonedCarts.data.length > 0 && (
                <button
                  type="button"
                  disabled={sendAllEmailMutation.isPending}
                  onClick={() => {
                    if (window.confirm('Bekleyen tüm terk edilmiş sepetlere hatırlatma e-postası göndermek istediğinize emin misiniz?')) {
                      sendAllEmailMutation.mutate();
                    }
                  }}
                  className="btn-secondary h-9 flex items-center gap-2 whitespace-nowrap text-xs cursor-pointer font-medium"
                >
                  <Send className="h-3.5 w-3.5 text-amber-600" />
                  <span>Bekleyen Tümüne Hatırlatma Gönder</span>
                </button>
              )}
            </div>

            {abandonedCarts.isLoading ? (
              <Skeleton className="h-40 w-full" />
            ) : abandonedCarts.data && abandonedCarts.data.length > 0 ? (
              <div className="overflow-x-auto mt-3">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-amber-200 text-left text-xs uppercase tracking-wide text-ink-600 bg-amber-100/40">
                      <th className="py-2.5 px-3 font-semibold rounded-l-md">Müşteri</th>
                      <th className="py-2.5 px-2 font-semibold">Ürünler</th>
                      <th className="py-2.5 px-2 text-right font-semibold">Tutar</th>
                      <th className="py-2.5 px-2 font-semibold">Son Hareket</th>
                      <th className="py-2.5 px-2 font-semibold">Hatırlatma Durumu</th>
                      <th className="py-2.5 px-3 text-right font-semibold rounded-r-md">Aksiyon</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-amber-100">
                    {abandonedCarts.data.map((cart) => (
                      <tr key={cart.cartId} className="hover:bg-amber-100/30 transition">
                        <td className="py-3 px-3">
                          <p className="font-medium text-ink-900">{cart.customerName}</p>
                          <p className="text-xs text-ink-500">{cart.email}</p>
                        </td>
                        <td className="py-3 px-2 max-w-[200px]">
                          <p className="truncate text-xs font-medium text-ink-800">
                            {cart.items.map((i) => `${i.productTitle} (${i.quantity})`).join(', ')}
                          </p>
                          <span className="text-[11px] text-ink-500">{cart.itemCount} parça ürün</span>
                        </td>
                        <td className="py-3 px-2 text-right font-semibold text-ink-900">
                          {money(cart.grandTotal, currency)}
                        </td>
                        <td className="py-3 px-2 text-xs text-ink-600">
                          {formatDate(cart.lastActivityAt)}
                        </td>
                        <td className="py-3 px-2">
                          {cart.abandonedEmailSentAt ? (
                            <div className="flex items-center gap-1.5 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2.5 py-1 w-max font-medium">
                              <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
                              <span>{cart.abandonedEmailCount}. Gönderildi ({formatDate(cart.abandonedEmailSentAt).slice(0, 10)})</span>
                            </div>
                          ) : (
                            <div className="flex items-center gap-1.5 text-xs text-amber-800 bg-amber-100/80 border border-amber-300 rounded-full px-2.5 py-1 w-max font-medium">
                              <Clock className="h-3.5 w-3.5 text-amber-600" />
                              <span>Henüz Gönderilmedi</span>
                            </div>
                          )}
                        </td>
                        <td className="py-3 px-3 text-right">
                          <button
                            type="button"
                            disabled={sendEmailMutation.isPending}
                            onClick={() => sendEmailMutation.mutate(cart.cartId)}
                            className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-3 py-1.5 text-xs font-medium text-white shadow-sm hover:bg-brand-700 disabled:opacity-50 transition cursor-pointer"
                          >
                            <Send className="h-3.5 w-3.5" />
                            <span>Hatırlatma E-Postası Gönder</span>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="py-8 text-center text-sm text-ink-500 bg-white/60 rounded-lg border border-dashed border-amber-200 mt-2">
                Terk edilmiş sepet bulunmuyor.
              </div>
            )}
          </section>

          {/* Stok Uyarıları */}
          {lowStock.data && lowStock.data.length > 0 ? (
            <section className="card p-5 border-red-100">
              <div className="mb-4 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-red-600" />
                  <h2 className="text-sm font-semibold text-ink-900">Kritik Stok Uyarısı</h2>
                </div>
                <Link to="/stok" className="text-xs text-brand-700 hover:underline font-medium">
                  Stok Yönetimine Git →
                </Link>
              </div>

              <div className="grid gap-3 sm:grid-cols-2 md:grid-cols-4">
                {lowStock.data.map((alert) => (
                  <div
                    key={alert.productVariantId}
                    className="flex items-center gap-3 rounded-lg border border-ink-200 bg-white p-3 shadow-xs"
                  >
                    <div className="h-10 w-10 shrink-0 overflow-hidden rounded bg-ink-50 border border-ink-100">
                      {alert.imageUrl ? (
                        <img src={alert.imageUrl} alt="" className="h-full w-full object-cover" />
                      ) : (
                        <Package className="h-5 w-5 m-auto text-ink-300" />
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-xs font-semibold text-ink-900">{alert.productTitle}</p>
                      <p className="truncate text-[11px] text-ink-500">{alert.variantTitle ?? alert.sku}</p>
                    </div>
                    <span
                      className={`badge font-semibold ${
                        alert.available <= 0 ? 'bg-red-100 text-red-800' : 'bg-amber-100 text-amber-800'
                      }`}
                    >
                      {alert.available} adet
                    </span>
                  </div>
                ))}
              </div>
            </section>
          ) : null}
        </>
      )}
    </div>
  );
}

function TaskTile({ to, label, value }: { to: string; label: string; value: number }) {
  return (
    <Link
      to={to}
      className={`flex items-center justify-between rounded-xl border bg-white p-4 transition hover:shadow-card ${
        value > 0 ? 'border-amber-200 bg-amber-50/20' : 'border-ink-200'
      }`}
    >
      <span className="text-sm font-medium text-ink-700">{label}</span>
      <span
        className={`text-lg font-bold rounded-lg px-2.5 py-0.5 ${
          value > 0 ? 'bg-amber-100 text-amber-800' : 'bg-ink-100 text-ink-500'
        }`}
      >
        {value}
      </span>
    </Link>
  );
}

function OrderStatusTag({ status, financial }: { status: string; financial: string }) {
  if (status === 'Cancelled') {
    return (
      <span className="inline-flex items-center rounded-md bg-red-50 px-2 py-0.5 text-xs font-medium text-red-700 border border-red-200">
        İptal Edildi
      </span>
    );
  }
  if (financial === 'Paid') {
    return (
      <span className="inline-flex items-center rounded-md bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700 border border-emerald-200">
        Ödendi
      </span>
    );
  }
  if (financial === 'Pending') {
    return (
      <span className="inline-flex items-center rounded-md bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700 border border-amber-200">
        Ödeme Bekliyor
      </span>
    );
  }
  return (
    <span className="inline-flex items-center rounded-md bg-ink-100 px-2 py-0.5 text-xs font-medium text-ink-700">
      {status}
    </span>
  );
}
